import { Injectable } from '@angular/core';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

/** Legacy parents for items moved out of Administration. */
/** Equivalent module keys used across old and new menu layouts. */
const NAV_MODULE_ALIASES: Record<string, string[]> = {
  product_configuration: ['product'],
  product: ['product_configuration'],
};

const NAV_PARENT_FALLBACKS: Record<string, string[]> = {
  form_builder: ['setting'],
  email_template: ['setting', 'campaign'],
  whatsapp_template: ['setting', 'campaign'],
  contact_group: ['setting', 'campaign'],
  product_configuration: ['setting', 'product'],
  product_categories: ['setting', 'product'],
  tax_configuration: ['setting', 'product'],
  product: ['setting', 'product_configuration'],
  woocommerce: ['setting'],
  woo_products: ['setting', 'woocommerce'],
  woo_orders: ['setting', 'woocommerce'],
};

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private permissions: any[] = [];

  /**
   * GLOBAL BYPASS FLAG: When set to true, bypasses all module, feature, and action permission checks.
   */
  public bypassAllFeatures = false;

  constructor(private localStorage: LocalStorageService) {
    this.loadPermissions();
  }

  /* ==============================
     LOAD / REFRESH
  ============================== */

  loadPermissions(): void {
    const raw = this.localStorage.getPermissions();
    this.permissions = this.normalizePermissions(raw);
  }

  refresh(): void {
    this.loadPermissions();
  }

  getAllPermissions(): any[] {
    return this.permissions;
  }

  /* ==============================
     MODULE ACCESS
  ============================== */

  hasModuleAccess(module: string): boolean {
    if (this.bypassAllFeatures) return true;
    this.ensureLoaded();

    const variants = this.getModuleVariants(module);
    const modulePerm = this.permissions.find(
      (p: any) =>
        this.isActiveFlag(p?.active) &&
        this.matchesModuleKey(this.readModuleKey(p), variants),
    );

    return !!modulePerm;
  }

  /**
   * Sidebar and route visibility from the role's module active true/false flag,
   * plus nested features and legacy parent grants. Subscription modules are used
   * only when the user has no role permission list.
   */
  hasNavAccess(
    module?: string,
    parentModules: string[] = [],
    allowParentModule = false,
  ): boolean {
    if (this.bypassAllFeatures) return true;
    if (!module) return true;
    this.ensureLoaded();

    const variants = this.expandModuleVariants(module);
    const parentHints = [
      ...parentModules,
      ...(NAV_PARENT_FALLBACKS[module] || []),
      ...(NAV_PARENT_FALLBACKS[this.normalizeModuleKey(module)] || []),
    ];

    if (this.isExplicitlyDenied(variants)) {
      return false;
    }

    for (const key of variants) {
      if (this.hasModuleAccess(key)) return true;
    }

    if (
      this.permissions.some(
        (p: any) =>
          this.isActiveFlag(p?.active) &&
          p.features?.some(
            (f: any) =>
              this.isActiveFlag(f?.active) &&
              this.matchesModuleKey(this.readFeatureKey(f), variants),
          ),
      )
    ) {
      return true;
    }

    for (const parent of parentHints) {
      for (const key of variants) {
        if (this.hasChildAccess(parent, key)) return true;
      }
    }

    if (allowParentModule) {
      for (const parent of parentHints) {
        if (this.hasModuleAccess(parent)) return true;
      }
    }

    // A saved role is the source of truth: each module is on only when its
    // active flag is true. Subscription access applies when no role list exists.
    if (this.permissions.length > 0) {
      return false;
    }

    if (this.isInSubscriptionModules(variants)) {
      return true;
    }

    return false;
  }

  /* ==============================
     FEATURE ACCESS
  ============================== */

  /**
   * Returns true if an active permission record exists for the given module.
   * Used to distinguish "RBAC not configured" (no record → permissive)
   * from "RBAC configured but feature disabled" (record exists, feature = false).
   */
  hasModuleRecord(module: string): boolean {
    if (this.bypassAllFeatures) return true;
    this.ensureLoaded();
    return this.permissions.some(
      (p: any) => p.module_name === module && this.isActiveFlag(p?.active),
    );
  }

  hasChildAccess(parentModule: any, childModule: any): boolean {
    if (this.bypassAllFeatures) return true;
    this.ensureLoaded();

    const parentKey = this.normalizeModuleKey(String(parentModule ?? ''));
    const childVariants = this.getModuleVariants(String(childModule ?? ''));

    const modulePerm = this.permissions.find(
      (p: any) =>
        this.normalizeModuleKey(this.readModuleKey(p)) === parentKey &&
        this.isActiveFlag(p?.active),
    );

    return (
      modulePerm?.features?.some(
        (f: any) =>
          this.isActiveFlag(f?.active) &&
          this.matchesModuleKey(this.readFeatureKey(f), childVariants),
      ) ?? false
    );
  }

  hasFeatureAccess(module: string, feature?: string): boolean {
    if (this.bypassAllFeatures) return true;
    this.ensureLoaded();

    const modulePerm = this.permissions.find(
      (p: any) => p.module_name === module && this.isActiveFlag(p?.active),
    );

    if (!modulePerm) return false;

    if (!feature) return true;

    return (
      modulePerm.features?.some(
        (f: any) => f.name === feature && this.isActiveFlag(f?.active),
      ) ?? false
    );
  }

  /* ==============================
     ACTION ACCESS
     Example: lead_create
  ============================== */

  hasActionAccess(module: string, actionKey: string): boolean {
    if (this.bypassAllFeatures) return true;
    this.ensureLoaded();

    const modulePerm = this.permissions.find(
      (p: any) => p.module_name === module && this.isActiveFlag(p?.active),
    );

    if (!modulePerm) return false;

    const inActions = modulePerm.actions?.some(
      (a: any) => a.name === actionKey && this.isActiveFlag(a?.active),
    );

    if (inActions) return true;

    const inFeatures = modulePerm.features?.some(
      (f: any) => f.name === actionKey && this.isActiveFlag(f?.active),
    );

    return inFeatures ?? false;
  }

  /* ==============================
     GET ACTIVE MODULES
  ============================== */

  getFeatureModules(): string[] {
    this.ensureLoaded();

    return this.permissions
      .filter((p: any) => this.isActiveFlag(p?.active))
      .map((p: any) => p.module_name);
  }

  /* ==============================
     HELPERS
  ============================== */

  private ensureLoaded(): void {
    if (!this.permissions || this.permissions.length === 0) {
      this.loadPermissions();
    }
  }

  private normalizePermissions(raw: any): any[] {
    let parsed = this.safeJsonParse(raw, raw);

    if (typeof parsed === 'string') {
      parsed = this.safeJsonParse(parsed, []);
    }

    if (Array.isArray(parsed)) {
      return parsed;
    }

    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed).map(([key, value]: [string, any]) => {
        if (value && typeof value === 'object') {
          return {
            module_name: value.module_name || value.module || value.name || key,
            active: value.active,
            features: value.features || [],
            actions: value.actions || [],
          };
        }
        return {
          module_name: key,
          active: value === true || value === 1 || value === 'true',
          features: [],
          actions: [],
        };
      });
    }

    return [];
  }

  private normalizeModuleKey(key: string): string {
    return String(key ?? '')
      .trim()
      .toLowerCase();
  }

  private getModuleVariants(module: string): string[] {
    const base = this.normalizeModuleKey(module);
    const variants = new Set<string>([module, base]);
    variants.add(base.replace(/-/g, '_'));
    variants.add(base.replace(/_/g, '-'));
    return Array.from(variants).filter(Boolean);
  }

  private expandModuleVariants(module: string): string[] {
    const variants = new Set<string>(this.getModuleVariants(module));
    const aliases =
      NAV_MODULE_ALIASES[module] ||
      NAV_MODULE_ALIASES[this.normalizeModuleKey(module)] ||
      [];

    aliases.forEach((alias) => {
      this.getModuleVariants(alias).forEach((variant) => variants.add(variant));
    });

    return Array.from(variants).filter(Boolean);
  }

  private matchesModuleKey(value: string, variants: string[]): boolean {
    const normalized = this.normalizeModuleKey(value);
    return variants.some(
      (variant) => this.normalizeModuleKey(variant) === normalized,
    );
  }

  private readModuleKey(entry: any): string {
    return String(entry?.module_name ?? entry?.module ?? entry?.name ?? '');
  }

  private readFeatureKey(entry: any): string {
    return String(entry?.name ?? entry?.feature_key ?? entry?.key ?? '');
  }

  private isActiveFlag(value: any): boolean {
    return value !== false && value !== 0 && value !== 'false' && value !== '0';
  }

  private isExplicitlyDenied(variants: string[]): boolean {
    return this.permissions.some(
      (p: any) =>
        p?.active === false &&
        this.matchesModuleKey(this.readModuleKey(p), variants),
    );
  }

  private isInSubscriptionModules(variants: string[]): boolean {
    const modules = this.getSubscriptionModules();
    return modules.some((entry) =>
      this.matchesModuleKey(String(entry), variants),
    );
  }

  private getSubscriptionModules(): string[] {
    try {
      const raw = this.localStorage.getFeatureModules();
      if (raw == null || raw === '') return [];

      if (Array.isArray(raw)) {
        return raw.map(String);
      }

      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed.map(String);
          }
          if (parsed && typeof parsed === 'object') {
            if (Array.isArray(parsed.modules)) {
              return parsed.modules.map(String);
            }
            return Object.keys(parsed);
          }
        } catch {
          return raw
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);
        }
      }
    } catch {
      return [];
    }

    return [];
  }

  private safeJsonParse(value: any, fallback: any): any {
    try {
      const parsed = JSON.parse(value);
      return parsed ?? fallback;
    } catch {
      return fallback;
    }
  }

  /**
   * Get access object for a module
   */
  getAccessObject(module: string): any {
    if (this.bypassAllFeatures) {
      return {
        create: true,
        update: true,
        delete: true,
        export: true,
        import: true,
        filter: true,
        view: true,
        configure: true,
        user: true,
        role_based_access_control: true,
        email_template: true,
        whatsapp_template: true,
        contact_group: true,
        pipeline: true,
        lead_activity: true,
        task: true,
        notes: true,
        meetings: true,
        communication: true,
        lead_profile_view: true,
        follow_up: true,
        tabs: true,
        convert_account: true,
        deal_view: true,
        lead_view: true,
      };
    }

    this.ensureLoaded();

    const accessObjects: { [key: string]: any } = {
      analytics: {
        deal_view: this.hasActionAccess('analytics', 'deal_analytics_read'),
        lead_view: this.hasActionAccess('analytics', 'lead_analytics_read'),
      },
      lead: (() => {
        // If no lead permission record exists, RBAC is unconfigured → allow everything.
        // If a record exists, respect the actual feature-level permissions.
        const leadConfigured = this.hasModuleRecord('lead');
        const feat = (key: string) =>
          leadConfigured ? this.hasFeatureAccess('lead', key) : true;
        const act = (key: string) =>
          leadConfigured ? this.hasActionAccess('lead', key) : true;
        return {
          create: act('lead_create'),
          update: act('lead_update'),
          delete: act('lead_delete'),
          export: act('lead_export'),
          import: act('lead_import'),
          filter: act('lead_filter'),
          lead_activity: feat('lead_activity'),
          task: feat('lead_task'),
          notes: feat('lead_notes'),
          meetings: feat('lead_meetings'),
          communication: feat('lead_communication'),
          lead_profile_view: feat('lead_profile_view'),
          follow_up: feat('lead_follow_up'),
          tabs: feat('lead_tab'),
          convert_account: feat('convert_account'),
        };
      })(),
      contacts: {
        create: this.hasActionAccess('contacts', 'contacts_create'),
        update: this.hasActionAccess('contacts', 'contacts_update'),
        delete: this.hasActionAccess('contacts', 'contacts_delete'),
        export: this.hasActionAccess('contacts', 'contacts_export'),
        import: this.hasActionAccess('contacts', 'contacts_import'),
        filter: this.hasActionAccess('contacts', 'contacts_filter'),
      },
      account: {
        create: this.hasActionAccess('account', 'account_create'),
        update: this.hasActionAccess('account', 'account_update'),
        delete: this.hasActionAccess('account', 'account_delete'),
        export: this.hasActionAccess('account', 'account_export'),
        import: this.hasActionAccess('account', 'account_import'),
        filter: this.hasActionAccess('account', 'account_filter'),
      },
      deals: {
        create: this.hasActionAccess('deals', 'deals_create'),
        update: this.hasActionAccess('deals', 'deals_update'),
        delete: this.hasActionAccess('deals', 'deals_delete'),
        export: this.hasActionAccess('deals', 'deals_export'),
        import: this.hasActionAccess('deals', 'deals_import'),
        filter: this.hasActionAccess('deals', 'deals_filter'),
      },
      campaign: {
        create: this.hasActionAccess('campaign', 'campaign_create'),
        update: this.hasActionAccess('campaign', 'campaign_update'),
        delete: this.hasActionAccess('campaign', 'campaign_delete'),
        export: this.hasActionAccess('campaign', 'campaign_export'),
      },
      workflow_automation: this.workflowAccess(),
      project: {
        create: this.hasActionAccess('project', 'project_create'),
        update: this.hasActionAccess('project', 'project_update'),
        delete: this.hasActionAccess('project', 'project_delete'),
        export: this.hasActionAccess('project', 'project_export'),
      },
    };

    return accessObjects[module] || {};
  }

  /**
   * Turning the module on grants create, update and delete.
   * A role that stores workflow actions is limited to the ones that are on.
   */
  private workflowAccess(): { create: boolean; update: boolean; delete: boolean } {
    const denied = { create: false, update: false, delete: false };
    if (this.bypassAllFeatures) {
      return { create: true, update: true, delete: true };
    }
    this.ensureLoaded();
    const perm = this.permissions.find(
      (p: any) =>
        this.normalizeModuleKey(this.readModuleKey(p)) === 'workflow_automation' &&
        this.isActiveFlag(p?.active),
    );
    if (!perm) return denied;
    const named = (perm.actions || []).filter((action: any) =>
      String(action?.name || '').startsWith('workflow_automation_'),
    );
    if (!named.length) {
      return { create: true, update: true, delete: true };
    }
    const on = (key: string) =>
      named.some((action: any) => action.name === key && this.isActiveFlag(action.active));
    return {
      create: on('workflow_automation_create'),
      update: on('workflow_automation_update'),
      delete: on('workflow_automation_delete'),
    };
  }
}
